import React, { useState } from 'react';
import { Box, Typography, TextField, CircularProgress } from '@mui/material';
import { Modal, Button } from '../Common';

interface RequestMoreInfoModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (message: string) => Promise<void>;
  applicationName: string;
}

export const RequestMoreInfoModal: React.FC<RequestMoreInfoModalProps> = ({
  open,
  onClose,
  onSubmit,
  applicationName,
}) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit(message.trim());
      setMessage('');
      onClose();
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMessage('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Request More Information"
      width={600}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Typography sx={{ fontSize: '14px', color: 'var(--palette-light-text-secondary-value-hex)' }}>
          Send a message to <strong>{applicationName}</strong> requesting additional information about their application.
        </Typography>

        <TextField
          label="Message to Vendor"
          multiline
          rows={6}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setError(null);
          }}
          placeholder="Please provide more details about..."
          error={!!error}
          helperText={error}
          disabled={loading}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            bordered
            size="content"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !message.trim()}
            primary
            size="content"
          >
            {loading ? <CircularProgress size={20} /> : 'Send Request'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
